import React from 'react';
import { useCampaignCapabilitiesStore, useCampaignUiStore, campaignKeys } from '@discovr/features';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Shared approve/reject logic for one FieldChange — the single place that
 * calls reviewEditRequest.approve/reject, invalidates the edit-request
 * queries, and surfaces a failure toast. Two surfaces render approve/reject
 * controls for the same field (ChangeRow's inline buttons in
 * EditRequestReviewPanel, and CommentThreadPopover's own popover controls —
 * an intentional product decision to keep both, not a bug); before this hook
 * each surface reimplemented this logic independently, which had already
 * started drifting (see the duplicated STATUS_BADGE/STATUS_STYLE color maps
 * across three files). This hook is the fix for the LOGIC duplication only —
 * it does not change what either surface looks like or removes either one.
 *
 * `approve` accepts an optional comment because CommentThreadPopover posts
 * whatever's in its draft textarea before approving (the BE's Approve call
 * has no comment param of its own); ChangeRow never has a comment box next
 * to its Approve button, so it simply omits the argument.
 */
export function useFieldReviewDecision(requestId: string, fieldPath: string) {
  const { addFieldComment, reviewEditRequest } = useCampaignCapabilitiesStore();
  const { campaignId } = useCampaignUiStore();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = React.useState(false);

  const invalidate = () => {
    if (!campaignId) return;
    queryClient.invalidateQueries({ queryKey: campaignKeys.editRequestCurrent(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.editRequestHistory(campaignId) });
  };

  const approve = async (comment?: string) => {
    if (!reviewEditRequest) return;
    setDeciding(true);
    try {
      if (comment) {
        await addFieldComment?.(requestId, fieldPath, comment);
      }
      await reviewEditRequest.approve(requestId, fieldPath);
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve');
      throw err;
    } finally {
      setDeciding(false);
    }
  };

  const reject = async (reason: string) => {
    if (!reviewEditRequest) return;
    setDeciding(true);
    try {
      // Reject's reason is optional and becomes the next comment on the
      // thread server-side (spec requirement #7) — no separate comment call.
      await reviewEditRequest.reject(requestId, fieldPath, reason);
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject');
      throw err;
    } finally {
      setDeciding(false);
    }
  };

  return {
    approve,
    reject,
    deciding,
    canDecide: Boolean(reviewEditRequest),
    // Exposed for callers that write through a DIFFERENT capability (e.g.
    // CommentThreadPopover's plain addFieldComment, outside approve/reject)
    // but still need the same edit-request queries refreshed afterward.
    invalidate,
  };
}
